import Image from 'next/image'
import Marquee from '@/components/ui/Marquee'

import { Icon } from '@/icons/Icon'

import { cn } from '@/lib/utils'

export const generateRating = (rating: number) => {
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 >= 0.3
  const totalStars = 5

  return (
    <div className="mb-1 flex gap-1 text-primary-500">
      {Array.from({ length: fullStars }).map((_, index) => (
        <Icon name="star-fill" key={`full-${index}`} />
      ))}
      {hasHalfStar && <Icon name="star-half" key="half" />}
      {Array.from({ length: totalStars - fullStars - (hasHalfStar ? 1 : 0) }).map((_, index) => (
        <Icon name="star" key={`empty-${index}`} />
      ))}
    </div>
  )
}

type TestimonialsProps = TestimonialType & {
  testimonials: any[]
}

type TestimonialProps = TestimonialType & {
  testimonial: any
}

type TestimonialType = {
  type?: 'default' | 'expanded'
  duration?: number
}

export default function Testimonials({
  testimonials,
  type = 'default',
  duration,
}: TestimonialsProps) {
  return (
    <Marquee className="py-8" pauseOnHover={true} duration={duration}>
      <div className={cn('flex items-start', type === 'expanded' ? 'gap-3 pl-3' : 'gap-6 pl-6')}>
        {testimonials.map((testimonial, index) => (
          <Testimonial key={index} testimonial={testimonial} type={type} />
        ))}
      </div>
    </Marquee>
  )
}

export function Testimonial({ testimonial, type }: TestimonialProps) {
  switch (type) {
    case 'expanded':
      return (
        <div className="flex w-64 shrink-0 cursor-default flex-col items-start gap-4 rounded-2xl border border-gray-200 p-6 transition-all duration-300 hover:border-gray-300 hover:shadow-xl dark:border-white/10 dark:hover:border-white/20">
          {generateRating(testimonial.rating)}
          <p className="text-14 leading-8 text-gray-600 dark:text-white/60">{testimonial.text}</p>

          <div className="flex w-full items-center gap-4">
            {testimonial.avatar && (
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full border border-black/10 dark:border-white/10">
                <Image
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  fill
                  sizes="40px"
                  className="object-cover"
                />
              </div>
            )}

            <div className="flex w-full grow flex-col gap-1.5 truncate">
              <p className="w-full truncate text-14 font-medium leading-tight text-gray-950 dark:text-white">
                {testimonial.name}
              </p>
              <p className="w-full truncate text-13 leading-tight text-gray-500 dark:text-white/50">
                {testimonial.role}
              </p>
            </div>
          </div>
        </div>
      )
    default:
      return (
        <div className="flex w-80 shrink-0 cursor-pointer flex-col items-center gap-3 rounded-2xl border border-gray-200 px-7 py-4 transition-all duration-300 hover:border-gray-300 hover:shadow-xl dark:border-white/15">
          {generateRating(testimonial.rating)}
          <p className="text-center text-14 leading-8 text-gray-600 dark:text-white/60">
            {testimonial.text}
          </p>
          <p className="text-13 font-semibold text-gray-950 dark:text-white">
            {testimonial.handle}
          </p>
        </div>
      )
  }
}
